import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
} from 'react';

import type { ValidationState } from '../../validation/types.js';
import { FormContext } from '../context/FormContext.js';
import type {
  ControlRegistrationHandle,
  ValidateFieldOptions,
} from '../form.types.js';
import type { FormRuntime } from '../runtime/createFormRuntime.js';
import { getFormById, subscribeToFormById } from '../runtime/formRegistry.js';

const noopSubscribe = () => () => undefined;

/**
 * The control-provided contract, with callbacks read live so registration
 * stays stable across renders.
 * @internal
 */
export interface FormRuntimeRegistrationOptions {
  /** Logical form-control id. */
  id: string;
  /** Submit key; snapshot values are keyed by `name ?? id`. */
  name?: string;
  /** Associates with a `<Form>` by id when rendered outside its subtree. */
  form?: string;
  /** Reads the control's current value. */
  getValue: () => unknown;
  /** Focuses the control (focus-first-invalid). */
  focus: () => void;
  /** Runs the control's own validation and resolves with its state. */
  validateField: (options: ValidateFieldOptions) => Promise<ValidationState>;
  /** Clears form-managed visual validation state while keeping the value. */
  resetValidation?: () => void;
}

/**
 * The shared registration primitive behind `useFormRegistration`
 * (public, custom controls) and `useInternalFormRegistration` (internal, Strato
 * controls). Resolves the enclosing runtime — from context, or by `form` id for
 * out-of-subtree controls — registers once, and keeps the descriptor callbacks
 * live via refs so a re-render never re-registers.
 *
 * Returns a `reportValidity` that pushes live validity between submits, or
 * `undefined` when there is no form to register with (standalone control).
 * @internal
 */
export function useFormRuntimeRegistration(
  options: FormRuntimeRegistrationOptions,
): ((state: ValidationState) => void) | undefined {
  const context = useContext(FormContext);
  const formId = options.form;

  // Out-of-subtree association: the control may resolve its form by id before
  // the <Form> registers (no effect ordering guarantee across sibling trees),
  // so subscribe and re-resolve once a form registers under this id.
  const byIdRuntime = useSyncExternalStore(
    useCallback(
      (onStoreChange) =>
        formId ? subscribeToFormById(formId, onStoreChange) : noopSubscribe(),
      [formId],
    ),
    () => (formId ? getFormById(formId) : undefined),
    () => undefined,
  );

  const runtime: FormRuntime | undefined = context?.runtime ?? byIdRuntime;

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const handleRef = useRef<ControlRegistrationHandle | undefined>(undefined);

  const { id } = options;

  useEffect(() => {
    if (!runtime || id === '') {
      return;
    }
    const handle = runtime.register({
      id,
      get name() {
        return optionsRef.current.name;
      },
      getValue: () => optionsRef.current.getValue(),
      focus: () => optionsRef.current.focus(),
      validateField: (validateOptions) =>
        optionsRef.current.validateField(validateOptions),
      resetValidation: () => optionsRef.current.resetValidation?.(),
    });
    handleRef.current = handle;
    return () => {
      handle.unregister();
      handleRef.current = undefined;
    };
  }, [runtime, id]);

  if (!runtime) {
    return undefined;
  }

  return (state) => handleRef.current?.reportValidity(state);
}
